import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import {Loader2, ChevronLeft, Clock, UserIcon} from 'lucide-react';
import {formatFullName} from "../../utils/personUtils";
import { useTranslation } from "react-i18next";
import { useKeyboardNavigation } from './useKeyboardNavigation';
import {API_URL} from "../../services/apiService";

const SearchResults = ({results, loading, error, onResultClick, countdownSeconds}) => {
    const { t, i18n } = useTranslation();
    const resultsContainerRef = useRef(null);
    const resultRefs = useRef([]);

    // פונקצית המיון
    const compareNames = (a, b) => {
        const nameA = formatFullName(a, i18n.language) || '';
        const nameB = formatFullName(b, i18n.language) || '';

        return nameA.localeCompare(nameB, i18n.language, {
            sensitivity: 'base',
            numeric: true
        });
    };

    // מיון התוצאות - תמיד צריך להיות מערך, אפילו ריק
    const sortedResults = results ? [...results].sort(compareNames) : [];

    // העברנו את ה-Hook לכאן - לפני כל ה-returns
    const {selectedIndex} = useKeyboardNavigation(sortedResults, onResultClick, resultRefs, resultsContainerRef);

    if (loading) {
        return (
            <div className="flex justify-center items-center p-6 sm:p-8">
                <div className="text-center">
                    <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 text-[#8B5E3C] animate-spin mx-auto mb-2" />
                    <p className="text-xs sm:text-sm text-[#937c5d] animate-pulse">
                        {t('searchResults.searching')}
                    </p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 sm:p-6">
                <div className="bg-red-50 rounded-lg p-3 sm:p-4 text-center">
                    <p className="text-red-600 text-sm sm:text-base font-medium">{error}</p>
                </div>
            </div>
        );
    }

    if (!results || results.length === 0) {
        return (
            <div className="p-6 sm:p-8 text-center">
                <div className="bg-[#f8f5f1] rounded-lg p-4 sm:p-6">
                    <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-[#937c5d] mx-auto mb-2" />
                    <p className="text-sm sm:text-base text-[#4a3f30] font-medium">
                        {t('searchResults.noResults')}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="py-2 sm:py-3">
            <div className="flex items-center justify-between px-3 sm:px-4 py-2 border-b border-[#e6dfd4]">
                <span className="text-xs sm:text-sm font-medium text-[#4a3f30]">
                    {sortedResults.length} {t('resultsPage.results')}
                    {sortedResults.length === 1 && countdownSeconds !== null && (
                        <span className="mr-2 text-[#8B5E3C]">
                            {t('searchResults.autoRedirect', {seconds: countdownSeconds})}
                        </span>
                    )}
                </span>
            </div>

            <div
                ref={resultsContainerRef}
                className="max-h-[70vh] sm:max-h-96 overflow-y-auto scroll-smooth"
            >
                {sortedResults.map((person, index) => (
                    <Link
                        key={person.gedId}
                        to={person.path}
                        ref={el => resultRefs.current[index] = el}
                        onClick={onResultClick}
                        className={`
                            block transition-all duration-200 group relative
                            tap-highlight-transparent
                            ${selectedIndex === index
                            ? 'bg-[#f8f5f1]'
                            : 'hover:bg-[#f8f5f1]'
                        }
                            active:bg-[#e6dfd4]
                        `}
                    >
                        <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-[#e6dfd4] last:border-b-0">
                            <div className="flex items-center gap-2 sm:gap-3">
                                <div className={`
                                    w-8 h-8 sm:w-10 sm:h-10 rounded-full
                                    flex items-center justify-center transition-colors
                                    ${selectedIndex === index
                                    ? 'bg-[#d4c8b8]'
                                    : 'bg-[#e6dfd4] group-hover:bg-[#d4c8b8]'
                                }
                                `}>
                                    {person.images?.length > 0 ? (
                                        <img
                                            crossOrigin="use-credentials"
                                            src={`${API_URL}/image/download/${encodeURIComponent(person.images[0])}`}
                                            alt={formatFullName(person, i18n['language'])}
                                            className="w-full h-full rounded-full object-cover"
                                        />
                                    ) : (
                                        <UserIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#937c5d]" />
                                    )}
                                </div>

                                <div className="flex-1 min-w-0">
                                    <h3 className={`
                                        font-medium text-sm sm:text-base
                                        mb-0.5 sm:mb-1 truncate transition-colors
                                        ${selectedIndex === index
                                        ? 'text-[#8B5E3C]'
                                        : 'text-[#4a3f30] group-hover:text-[#8B5E3C]'
                                    }
                                    `}>
                                        {formatFullName(person, i18n['language'], true)}
                                    </h3>

                                    <div className="flex items-center gap-2">
                                        {person.isDifferentFamilyTree && (
                                            <span className="text-[10px] sm:text-xs bg-red-50 text-red-500
                                                         px-1.5 sm:px-2 py-0.5 rounded-full
                                                         border border-red-100 font-medium
                                                         whitespace-nowrap">
                                                {t('searchResults.otherFamilyTree')}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <ChevronLeft className={`
                                    w-4 h-4 sm:w-5 sm:h-5 text-[#937c5d] 
                                    transform transition-all duration-200
                                    hidden sm:block
                                    ${selectedIndex === index
                                    ? 'opacity-100 translate-x-0'
                                    : 'opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0'
                                }
                                `} />
                            </div>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
};

export default SearchResults;